import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import {
  FolderOpen as FolderOpenIcon,
  Person as PersonIcon,
  CorporateFare as BusinessIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { IconButton } from '@mui/material';
import Radio from '@mui/material/Radio';
import Checkbox from '@mui/material/Checkbox';
import '../../components/OrgSelect/OrgTreeSelect.css';

// 개별 사용자 행 (메모이제이션 및 disabled 지원)
const UserRow = React.memo(({ user, isChecked, isUserSelectable, isDisabled, selectionMode, onAction, showDept = false }) => {
  const handlePointerDown = (e) => {
    e.preventDefault();
    if (isDisabled) return;
    if (isUserSelectable) onAction(user, true, !isChecked);
  };

  return (
    <tr
      className={`${isChecked ? 'selected' : ''} ${isDisabled ? 'table-light' : ''}`}
      style={{
        cursor: isDisabled ? 'not-allowed' : (isUserSelectable ? 'pointer' : 'default'),
        userSelect: 'none',
        opacity: isDisabled ? 0.65 : 1,
        backgroundColor: isDisabled ? '#f8f9fa' : undefined
      }}
      title={isDisabled ? '선택할 수 없는 사용자입니다 (본인 또는 이미 등록됨)' : undefined}
    >
      {isUserSelectable && (
        <td className="text-center" onPointerDown={handlePointerDown}>
          {selectionMode === 'single' ? (
            <Radio checked={isChecked} disabled={isDisabled} size="small" sx={{ padding: '2px', pointerEvents: 'none' }} />
          ) : (
            <Checkbox checked={isChecked} disabled={isDisabled} size="small" sx={{ padding: '2px', pointerEvents: 'none' }} />
          )}
        </td>
      )}
      <td onPointerDown={handlePointerDown}>
        <div style={{ display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle', lineHeight: 1 }}>
          <span>{user.name}</span>
          {isDisabled && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '16px',
                padding: '0 5px',
                fontSize: '9px',
                fontWeight: '600',
                borderRadius: '3px',
                backgroundColor: '#fee2e2',
                color: '#dc2626',
                border: '1px solid #fca5a5',
                letterSpacing: '-0.3px',
                boxSizing: 'border-box',
                marginLeft: '6px',
                lineHeight: 1
              }}
            >
              선택불가
            </span>
          )}
        </div>
      </td>
      <td onPointerDown={handlePointerDown}>{user.position}</td>
      <td onPointerDown={handlePointerDown}>{user.userid}</td>
      {showDept && (
        <td onPointerDown={handlePointerDown} style={{ fontSize: '11px', color: '#888' }}>
          {user.dept_name}
        </td>
      )}
    </tr>
  );
});

/**
 * 업무협조 전용 OrgTreeSelect 컴포넌트
 * (공통모듈 OrgSelect 100% 무변경 보존 및 disabledUserIds 지원)
 */
export default function WorkOrderOrgTreeSelect({
  onConfirm,
  onCancel,
  selectionMode = "multiple",
  targetType = "all",
  initialSelected = [],
  disabledUserIds = []
}) {
  const [deptTree, setDeptTree] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allDeptCodes, setAllDeptCodes] = useState([]);
  const [expandedItems, setExpandedItems] = useState([]);

  const [deptSearchInput, setDeptSearchInput] = useState('');
  const [deptSearchTerm, setDeptSearchTerm] = useState('');

  const [userSearchInput, setUserSearchInput] = useState('');
  const [activeUserSearch, setActiveUserSearch] = useState('');

  const [selectedTargets, setSelectedTargets] = useState(() => {
    return initialSelected.filter(t => !disabledUserIds.some(d => String(d) === String(t.target_id)));
  });
  const [activeDept, setActiveDept] = useState(null);

  const { token, user } = useContext(AuthContext);

  useEffect(() => {
    const comCode = user?.M_ComCode || sessionStorage.getItem('M_ComCode');

    axios.get('/api/orgchart', {
      headers: {
        'x-com-code': comCode,
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => {
        const { tree, allDeptCodes: codes, map, flatUsers } = buildDeptTree(res.data);
        setDeptTree(tree);
        setAllUsers(flatUsers);
        setAllDeptCodes(codes);
        setExpandedItems(codes);

        let myDeptNode = null;
        if (user?.dept_code && map[user.dept_code]) {
          myDeptNode = map[user.dept_code];
        } else if (user?.userid) {
          const myDeptRaw = res.data.find(d => d.users?.some(u => String(u.userid) === String(user.userid)));
          if (myDeptRaw && map[myDeptRaw.dept_code]) {
            myDeptNode = map[myDeptRaw.dept_code];
          }
        }

        if (myDeptNode) {
          setActiveDept(myDeptNode);
        } else if (tree.length > 0) {
          setActiveDept(tree[0]);
        }
      })
      .catch(err => {
        console.error('조직도 불러오기 실패:', err);
      });
  }, [token, user]);

  const buildDeptTree = (depts) => {
    const map = {};
    const tree = [];
    const allDeptCodes = [];
    const flatUsers = [];

    depts.forEach(dept => {
      allDeptCodes.push(String(dept.dept_code));
      const usersWithDept = (dept.users || []).map(u => {
        const userObj = {
          ...u,
          dept_code: dept.dept_code,
          dept_name: dept.dept_name,
          full_dept_name: dept.full_dept_name
        };
        flatUsers.push(userObj);
        return userObj;
      });
      map[dept.dept_code] = { ...dept, children: [], users: usersWithDept };
    });

    depts.forEach(dept => {
      if (dept.parent_dept_code && map[dept.parent_dept_code]) {
        map[dept.parent_dept_code].children.push(map[dept.dept_code]);
      } else {
        tree.push(map[dept.dept_code]);
      }
    });

    return { tree, allDeptCodes, map, flatUsers };
  };

  const handleCheck = useCallback((item, isUser, checked) => {
    if (isUser) {
      const targetId = String(item.userid);
      if (disabledUserIds.some(d => String(d) === String(targetId) || String(d) === String(item.id))) {
        return;
      }

      const targetObj = {
        target_type: 'user',
        target_id: targetId,
        target_name: item.name,
        position: item.position || '',
        dept_name: item.dept_name || '',
        disp_target_name: `${item.name}${item.position ? " / " + item.position : ""}${item.dept_name ? " (" + item.dept_name + ")" : ""}`,
      };

      setSelectedTargets(prev => {
        if (selectionMode === "single") {
          return checked ? [targetObj] : [];
        } else {
          if (checked) {
            if (prev.some(t => String(t.target_id) === String(targetId))) return prev;
            return [...prev, targetObj];
          } else {
            return prev.filter(t => String(t.target_id) !== String(targetId));
          }
        }
      });
    } else {
      // 부서(Department)를 체크/해제한 경우: 직속 부서원들로 자동 전개
      const directUsers = item.users || [];
      if (directUsers.length === 0) {
        if (checked) alert('해당 부서에 소속된 직속 부서원이 없습니다.');
        return;
      }

      // 본인 및 차단 대상(disabledUserIds) 제외
      const validUsers = directUsers.filter(u => {
        const uid = String(u.userid);
        const id = String(u.id);
        return !disabledUserIds.some(d => String(d) === uid || String(d) === id);
      });

      if (validUsers.length === 0) {
        if (checked) alert('해당 부서의 모든 부서원이 이미 등록되었거나 제외 대상(본인/상대 목록)입니다.');
        return;
      }

      const userObjs = validUsers.map(u => ({
        target_type: 'user',
        target_id: String(u.userid),
        target_name: u.name,
        position: u.position || '',
        dept_name: u.dept_name || '',
        disp_target_name: `${u.name}${u.position ? " / " + u.position : ""}${u.dept_name ? " (" + u.dept_name + ")" : ""}`,
      }));

      setSelectedTargets(prev => {
        if (checked) {
          // 중복 없이 추가 (이미 존재하는 target_id는 제외)
          const existingIds = prev.map(p => String(p.target_id));
          const toAdd = userObjs.filter(n => !existingIds.includes(String(n.target_id)));
          return [...prev, ...toAdd];
        } else {
          // 해당 부서원 일괄 제거
          const removeIds = directUsers.map(u => String(u.userid));
          return prev.filter(t => !removeIds.includes(String(t.target_id)));
        }
      });
    }
  }, [selectionMode, disabledUserIds]);

  const removeTarget = (target_id, target_type) => {
    setSelectedTargets(prev => prev.filter(t => !(String(t.target_id) === String(target_id) && t.target_type === target_type)));
  };

  // 부서 검색 실행
  const runDeptSearch = () => {
    setDeptSearchTerm(deptSearchInput);
  };

  // 사용자 검색 실행
  const runUserSearch = () => {
    setActiveUserSearch(userSearchInput);
  };

  // 사용자 검색 초기화
  const clearUserSearch = () => {
    setUserSearchInput('');
    setActiveUserSearch('');
  };

  // 실제 표시할 사용자 목록 계산
  const displayUsers = useMemo(() => {
    if (!activeUserSearch) return activeDept?.users || [];
    const lowerSearch = activeUserSearch.toLowerCase();
    return allUsers.filter(u =>
      u.name.toLowerCase().includes(lowerSearch) ||
      String(u.userid).toLowerCase().includes(lowerSearch) ||
      (u.position && u.position.toLowerCase().includes(lowerSearch))
    );
  }, [activeUserSearch, activeDept, allUsers]);

  const renderTreeNodes = (nodes) => {
    return nodes.map(node => {
      const isDeptSelectable = true;
      const directUsers = (node.users || []).filter(u => {
        const uid = String(u.userid);
        const id = String(u.id);
        return !disabledUserIds.some(d => String(d) === uid || String(d) === id);
      });

      const isChecked = directUsers.length > 0 && directUsers.every(u => selectedTargets.some(t => String(t.target_id) === String(u.userid)));
      const isIndeterminate = !isChecked && directUsers.some(u => selectedTargets.some(t => String(t.target_id) === String(u.userid)));

      // 부서 검색 필터링
      if (deptSearchTerm && !node.dept_name.includes(deptSearchTerm)) {
        const hasMatchingChild = JSON.stringify(node.children).includes(deptSearchTerm);
        if (!hasMatchingChild) return null;
      }

      const labelContent = (
        <div
          style={{ display: 'flex', alignItems: 'center', padding: '4px 0', width: '100%' }}
          onClick={(e) => {
            e.stopPropagation();
            setActiveDept(node);
            clearUserSearch();
          }}
        >
          {isDeptSelectable && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                handleCheck(node, false, !isChecked);
              }}
              style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <Checkbox
                checked={isChecked}
                indeterminate={isIndeterminate}
                size="small"
                disableRipple
                tabIndex={-1}
                sx={{ padding: '2px', marginRight: '4px', pointerEvents: 'none' }}
              />
            </span>
          )}
          <FolderOpenIcon fontSize="small" sx={{ mr: 1, color: '#f6c23e' }} />
          <span style={{ fontWeight: activeDept?.dept_code === node.dept_code ? 'bold' : 'normal', color: '#333' }}>
            {node.dept_name}
          </span>
        </div>
      );

      return (
        <TreeItem key={String(node.dept_code)} itemId={String(node.dept_code)} label={labelContent}>
          {node.children?.length > 0 ? renderTreeNodes(node.children) : null}
        </TreeItem>
      );
    });
  };

  return ReactDOM.createPortal(
    <div className="org-tree-popup" onPointerDown={e => e.stopPropagation()}>
      <div className={`org-tree-popup-content ${targetType === 'dept' ? 'is-narrow' : ''}`}>
        <div className="org-tree-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <h5 className="mb-0">🏢 조직도 선택</h5>
            <span style={{ fontSize: '12px', color: '#0d6efd', backgroundColor: '#e7f1ff', padding: '3px 8px', borderRadius: '4px', fontWeight: '500' }}>
              💡 부서 체크 시 해당 부서의 <strong>'직속 부서원'</strong>만 추가됩니다. (하위 부서 제외)
            </span>
          </div>
          <IconButton onClick={onCancel} size="small"><CloseIcon /></IconButton>
        </div>

        <div className="org-tree-body">
          {/* 1. 트리 패널 (부서) */}
          <div className="org-tree-pane">
            <div className="org-tree-pane-header d-flex justify-content-between align-items-center">
              <span>조직 트리</span>
              <div className="d-flex gap-1">
                <button 
                  title="전체 펼치기"
                  className="btn btn-primary btn-xs p-0 d-flex align-items-center justify-content-center border-0 shadow-sm" 
                  style={{ width: '22px', height: '22px', fontSize: '14px', fontWeight: '900', borderRadius: '4px' }}
                  onClick={() => setExpandedItems(allDeptCodes)}
                >+</button>
                <button 
                  title="전체 접기"
                  className="btn btn-primary btn-xs p-0 d-flex align-items-center justify-content-center border-0 shadow-sm" 
                  style={{ width: '22px', height: '22px', fontSize: '14px', fontWeight: '900', borderRadius: '4px' }}
                  onClick={() => setExpandedItems([])}
                >-</button>
              </div>
            </div>
            <div className="p-2 border-bottom d-flex gap-1">
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="부서명 검색..."
                  value={deptSearchInput}
                  onChange={(e) => setDeptSearchInput(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') runDeptSearch(); }}
                  style={{ paddingRight: deptSearchInput ? '30px' : '8px' }}
                />
                {deptSearchInput && (
                  <IconButton
                    size="small"
                    onClick={() => { setDeptSearchInput(''); setDeptSearchTerm(''); }}
                    style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)', padding: '2px' }}
                  >
                    <CloseIcon style={{ fontSize: '14px' }} />
                  </IconButton>
                )}
              </div>
              <button
                className="btn btn-outline-secondary btn-xs"
                style={{ padding: '0 8px', display: 'flex', alignItems: 'center' }}
                onClick={runDeptSearch}
              >
                <SearchIcon style={{ fontSize: '16px' }} />
              </button>
            </div>
            <div className="org-tree-pane-content premium-tree-root">
              <SimpleTreeView expandedItems={expandedItems} onExpandedItemsChange={(e, items) => setExpandedItems(items)}>
                {renderTreeNodes(deptTree)}
              </SimpleTreeView>
            </div>
          </div>

          {/* 2. 중앙 패널 (사용자 검색 및 목록) */}
          {targetType !== "dept" && (
            <div className="org-tree-pane">
              <div className="org-tree-pane-header">
                <span>👤 {activeUserSearch ? '전사 사용자 검색 결과' : (activeDept ? activeDept.dept_name : '부서원')}</span>
                <span className="badge bg-secondary">{displayUsers.length}명</span>
              </div>
              <div className="p-2 border-bottom d-flex gap-1">
                <div style={{ position: 'relative', flex: 1 }}>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="찾으실 사원 성명 입력..."
                    value={userSearchInput}
                    onChange={(e) => setUserSearchInput(e.target.value)}
                    onKeyPress={(e) => { if (e.key === 'Enter') runUserSearch(); }}
                    style={{ paddingRight: userSearchInput ? '30px' : '8px' }}
                  />
                  {userSearchInput && (
                    <IconButton
                      size="small"
                      onClick={clearUserSearch}
                      style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)', padding: '2px' }}
                    >
                      <CloseIcon style={{ fontSize: '14px' }} />
                    </IconButton>
                  )}
                </div>
                <button
                  className="btn btn-outline-secondary btn-xs"
                  style={{ padding: '0 10px', display: 'flex', alignItems: 'center' }}
                  onClick={runUserSearch}
                >
                  <SearchIcon style={{ fontSize: '16px' }} />
                </button>
              </div>
              <div className="org-tree-pane-content p-0">
                <table className="premium-user-table">
                  <thead>
                    <tr>
                      <th width="40" className="text-center">선택</th>
                      <th>이름</th>
                      <th>직책</th>
                      <th>사번</th>
                      {activeUserSearch && <th>부서</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {displayUsers.length > 0 ? (
                      displayUsers.map(user => {
                        const isDis = disabledUserIds.some(d => String(d) === String(user.userid) || String(d) === String(user.id));
                        return (
                          <UserRow
                            key={user.userid}
                            user={user}
                            isChecked={selectedTargets.some(t => t.target_type === 'user' && t.target_id === String(user.userid))}
                            isUserSelectable={true}
                            isDisabled={isDis}
                            selectionMode={selectionMode}
                            onAction={handleCheck}
                            showDept={!!activeUserSearch}
                          />
                        );
                      })
                    ) : (
                      <tr><td colSpan="5" className="text-center text-muted py-4">조회된 사원이 없습니다.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 3. 우측 패널 */}
          <div className="org-tree-pane">
            <div className="org-tree-pane-header bg-primary text-white d-flex justify-content-between align-items-center">
              <div>
                <span>✅ 선택된 대상</span>
                <span className="badge bg-light text-primary ms-2">{selectedTargets.length}</span>
              </div>
              <button type="button" className="btn btn-outline-light btn-xs py-0 px-2" style={{ fontSize: '11px', height: '22px' }} onClick={() => setSelectedTargets([])}>전체삭제</button>
            </div>
            <div className="org-tree-pane-content" style={{ backgroundColor: '#f8f9fa' }}>
              {selectedTargets.length > 0 ? (
                <ul className="selected-list">
                  {selectedTargets.map((t, idx) => (
                    <li key={`${t.target_type}-${t.target_id}-${idx}`} className="selected-item shadow-sm">
                      <div className="selected-info">
                        {t.target_type === 'dept' ? <BusinessIcon fontSize="small" sx={{ color: '#1cc88a' }} /> : <PersonIcon fontSize="small" sx={{ color: '#4e73df' }} />}
                        <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                          <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={t.target_name}>{t.target_name}</div>
                          <div style={{ fontSize: '11px', color: '#6c757d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={t.disp_target_name}>{t.disp_target_name}</div>
                        </div>
                      </div>
                      <IconButton size="small" color="error" onClick={() => removeTarget(t.target_id, t.target_type)}><DeleteIcon fontSize="small" /></IconButton>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center text-muted mt-5"><p>선택된 대상이 없습니다.</p></div>
              )}
            </div>
          </div>
        </div>

        <div className="org-tree-footer">
          <button className="btn btn-success px-4" onClick={() => { onConfirm(selectedTargets); onCancel(); }}>확인</button>
          <button className="btn btn-secondary px-4" onClick={onCancel}>취소</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
